import React, { useState } from 'react';
import { Card, Input, Button, Form, message, Spin } from 'antd';
import axios from 'axios';
import moment from 'moment';

const MusicScraper = ({ 
  musicGenres, 
  selectedProfile, 
  setSelectedProfile, 
  showTrackForm, 
  setShowTrackForm, 
  trackForm, 
  setTrackData 
}) => {
  const [loading, setLoading] = useState(false);

  const extractItunesId = (url) => {
    // Handle different iTunes URL formats
    const patterns = [
      /\/id(\d+)/,  // Standard iTunes URL
      /\/album\/[^/]+\/(\d+)/,  // Album URL
      /\/track\/[^/]+\/(\d+)/,  // Track URL
      /\/song\/[^/]+\/(\d+)/,   // New Apple Music song URL
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  const findProfileByArtistName = (artistName, profiles) => {
    if (!artistName || !profiles.length) {
      console.log('No artist name or profiles available');
      return null;
    }
    
    console.log('Searching for artist:', artistName);
    console.log('Available profiles:', profiles.map(p => p.username));
    
    // Try exact match first
    let profile = profiles.find(p => {
      const match = p.username.toLowerCase() === artistName.toLowerCase();
      console.log(`Comparing "${p.username}" with "${artistName}": ${match}`);
      return match;
    });
    
    if (profile) {
      console.log('Found exact match:', profile);
      return profile;
    }
    
    // If no exact match, try partial match
    profile = profiles.find(p => {
      const usernameLower = p.username.toLowerCase();
      const artistLower = artistName.toLowerCase();
      const match = usernameLower.includes(artistLower) || artistLower.includes(usernameLower);
      console.log(`Partial match check - "${usernameLower}" with "${artistLower}": ${match}`);
      return match;
    });
    
    if (profile) {
      console.log('Found partial match:', profile);
    } else {
      console.log('No matching profile found');
    }
    
    return profile;
  };

  const fetchItunesTrackInfo = async (url, profiles) => {
    try {
      const trackId = extractItunesId(url);
      if (!trackId) {
        message.error('Invalid iTunes URL format');
        return null;
      }

      console.log('Fetching track with ID:', trackId);
      // Use CORS proxy for development with fallback
      const isDevelopment = window.location.hostname === 'localhost';
      const itunesUrl = `https://itunes.apple.com/lookup?id=${trackId}&entity=song`;
      
      let responseData = null;
      if (isDevelopment) {
        // Try multiple CORS proxies as fallback
        const corsProxies = [
          'https://corsproxy.io/?',
          'https://cors-anywhere.herokuapp.com/',
          'https://api.allorigins.win/get?url='
        ];
        
        for (const proxy of corsProxies) {
          try {
            console.log(`🎵 [MusicScraper] Trying proxy: ${proxy}`);
            let finalUrl;
            
            if (proxy.includes('allorigins')) {
              finalUrl = `${proxy}${encodeURIComponent(itunesUrl)}`;
              const response = await axios.get(finalUrl, { timeout: 10000 });
              responseData = JSON.parse(response.data.contents);
            } else {
              finalUrl = `${proxy}${itunesUrl}`;
              const response = await axios.get(finalUrl, { timeout: 10000 });
              responseData = response.data;
            }
            
            console.log(`🎵 [MusicScraper] Success with proxy: ${proxy}`);
            break;
          } catch (error) {
            console.log(`🎵 [MusicScraper] Failed with proxy: ${proxy}`, error.message);
            continue;
          }
        }
        
        if (!responseData) {
          throw new Error('All CORS proxies failed');
        }
      } else {
        // Production - direct call
        const response = await axios.get(itunesUrl);
        responseData = response.data;
      }
      
      console.log('iTunes API response:', responseData);

      if (!responseData.results || responseData.results.length === 0) {
        message.error('Track not found');
        return null;
      }

      const track = responseData.results[0];
      console.log('Track artist name:', track.artistName);
      
      // Get highest resolution artwork
      let coverUrl = track.artworkUrl100;
      if (coverUrl) {
        coverUrl = coverUrl
          .replace('100x100', '600x600')
          .replace('60x60', '600x600')
          .replace('30x30', '600x600');
      }

      // Try to find matching profile for the artist
      const matchingProfile = findProfileByArtistName(track.artistName, profiles);
      console.log('Matching profile result:', matchingProfile);
      
      if (matchingProfile) {
        console.log('Setting selected profile:', matchingProfile);
        setSelectedProfile(matchingProfile);
      } else {
        console.log('No matching profile found for artist:', track.artistName);
      }
      
      // Map iTunes data to our format
      const trackData = {
        name: track.trackName,
        artist: track.artistName,
        description: `${track.trackName} by ${track.artistName}${track.collectionName ? ` from ${track.collectionName}` : ''}`,
        genres: [track.primaryGenreName],
        label: track.recordLabel || track.artistName,
        releaseDate: track.releaseDate,
        duration: Math.floor(track.trackTimeMillis / 1000),
        buyLink: track.trackViewUrl,
        streamLink: track.previewUrl,
        image: coverUrl,
        trackType: track.wrapperType === 'track' ? 'Single' : 'Album',
        originCountry: track.country,
      };

      console.log('Processed track data:', trackData);
      return trackData;
    } catch (error) {
      console.error('Error fetching iTunes track:', error);
      message.error('Failed to fetch track information');
      return null;
    }
  };

  const onFinishTrack = async (values, profiles) => {
    try {
      setLoading(true);
      const trackData = await fetchItunesTrackInfo(values.url, profiles);
      
      if (!trackData) {
        setLoading(false);
        return;
      }

      setTrackData(trackData);
      message.success('Track information fetched successfully!');

      let musicTypes = trackData.genres?.map(genre => {
        const foundGenre = musicGenres.find(g => g.value.toLowerCase() === genre.toLowerCase());
        return foundGenre?._id;
      }).filter(Boolean);
      
      // If no musicTypes from iTunes, use selectedProfile's musicTypes
      if ((!musicTypes || musicTypes.length === 0) && selectedProfile && selectedProfile.musicTypes && selectedProfile.musicTypes.length > 0) {
        musicTypes = selectedProfile.musicTypes.map(mt => typeof mt === 'string' ? mt : mt._id);
      }
      
      const formValues = {
        title: trackData.name,
        artist: trackData.artist,
        description: trackData.description,
        musicTypes,
        label: trackData.label,
        releaseDate: trackData.releaseDate ? moment(trackData.releaseDate) : null,
        duration: trackData.duration,
        buyLink: trackData.buyLink,
        streamLink: trackData.streamLink,
        cover: trackData.image,
        trackType: trackData.trackType,
        originCountry: trackData.originCountry,
        isPublished: true,
      };
      
      if (selectedProfile) {
        formValues.creatorProfileId = selectedProfile._id;
      }
      
      trackForm.setFieldsValue(formValues);
      setShowTrackForm(true);
    } catch (error) {
      message.error('Failed to fetch track information: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Card title="Music Track Scraper" className="mb-4">
        <Form onFinish={(values) => onFinishTrack(values, [])} layout="vertical">
          <Form.Item
            name="url"
            label="iTunes Track URL"
            rules={[
              { required: true, message: 'Please input the track URL!' },
              { 
                pattern: /^https?:\/\/(?:itunes\.apple\.com|music\.apple\.com)/,
                message: 'Please enter a valid iTunes/Apple Music URL'
              }
            ]}
          >
            <Input 
              placeholder="https://music.apple.com/..." 
              onChange={(e) => {
                const url = e.target.value;
                if (url && url.match(/^https?:\/\/(?:itunes\.apple\.com|music\.apple\.com)/)) {
                  setLoading(true);
                  fetchItunesTrackInfo(url, []).then(data => {
                    if (data) {
                      setTrackData(data);
                      let musicTypes = data.genres?.map(genre => {
                        const foundGenre = musicGenres.find(g => g.value.toLowerCase() === genre.toLowerCase());
                        return foundGenre?._id;
                      }).filter(Boolean);
                      
                      // If no musicTypes from iTunes, use selectedProfile's musicTypes
                      if ((!musicTypes || musicTypes.length === 0) && selectedProfile && selectedProfile.musicTypes && selectedProfile.musicTypes.length > 0) {
                        musicTypes = selectedProfile.musicTypes.map(mt => typeof mt === 'string' ? mt : mt._id);
                      }
                      
                      const formValues = {
                        title: data.name,
                        artist: data.artist,
                        description: data.description,
                        musicTypes,
                        label: data.label,
                        releaseDate: data.releaseDate ? moment(data.releaseDate) : null,
                        duration: data.duration,
                        buyLink: data.buyLink,
                        streamLink: data.streamLink,
                        cover: data.image,
                        trackType: data.trackType,
                        originCountry: data.originCountry,
                        isPublished: true,
                      };
                      
                      if (selectedProfile) {
                        formValues.creatorProfileId = selectedProfile._id;
                      }
                      
                      trackForm.setFieldsValue(formValues);
                      setShowTrackForm(true);
                    }
                    setLoading(false);
                  }).catch(error => {
                    console.error('Error in onChange handler:', error);
                    setLoading(false);
                  });
                }
              }}
            />
          </Form.Item>
        </Form>
      </Card>

      {loading && (
        <div className="text-center">
          <Spin size="large" />
          <p className="mt-2">Fetching track information...</p>
        </div>
      )}
    </div>
  );
};

export default MusicScraper; 